import { ChangeEvent, useEffect, useReducer, useState } from "react";
import { Header, CustomTable, WarningDialog } from "../../../components";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { getStaticTexts, PERMISSIONS } from "../../../constants";
import { LostItemStates } from "../../../interfaces";
import { enqueueSnackbar } from "notistack";
import {
  getItems,
  resolveItem,
  updateItemStatus,
} from "../../../services/aggregation";
import { handleCharacterLimit } from "../../../services/common";
import { useSelector } from "react-redux";
import Loader from "../../../components/Loader";
import { withPermission } from "../../../components/hoc";
import { Manage } from "./manage";
import { getChatListing } from "../../../services/chats";
import ChatDetails from "../../../components/ChatDetails";
import { formatDate } from "../../../services/utils";

const LostItems = () => {
  const language = useSelector((state: any) => state.profile.defaultLanguage);
  const staticTexts = getStaticTexts(language);
  const userPermissions = useSelector((state: any) => state.permissions);
  const [showChatListing, setShowChatListing] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isEditIconDisabled = !userPermissions.includes(
    PERMISSIONS.EDIT_LOST_ITEMS
  );
  const isDeleteIconDisabled = !userPermissions.includes(
    PERMISSIONS.DELETE_LOST_ITEMS
  );

  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout>();
  const categories = useSelector((state: any) => state.category);

  const lostItemReducer = (state: LostItemStates, action: any) => {
    switch (action.type) {
      case "LOADING":
        return { ...state, loading: true };
      case "LOADED":
        return { ...state, loading: false, list: action.list };
      case "UPDATE_FIELDS":
        return { ...state, ...action.payload };
      case "UPDATE_STATUS":
        return { ...state, loading: true };
        case 'TOGGLE_STATUS':
          const { id, status } = action.payload;
          const updatedList = state.list.map((user: any) =>
            user._id === id
              ? {
                  ...user,
                  status: (
                    <Switch
                      checked={status === "ACTIVE"}
                      disabled={isEditIconDisabled}
                      onChange={(e) => {
                        handleStatusChange(
                          user._id,
                          e.target.checked ? "ACTIVE" : "INACTIVE",
                        );
                      }}
                    />
                  ),
                }
              : user
          );
          return {
            ...state,
            list: updatedList,
          };
        
      default:
        throw new Error(`Unhandled action type: ${action.type}`);
    }
  };

  const [state, dispatch] = useReducer(lostItemReducer, {
    _item: "",
    list: [],
    selectAll: [],
    manage: {
      isOpen: false,
      type: "view",
      item: null,
    },
    loading: true,
    searchQuery: "",
    page: 1,
    statusFilter: "",
    totalPages: 1,
    categoryFilter: "",
    claimToUserId: "",
  });

  const handleManage = (type: "new" | "edit" | "view", item: any = null) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        manage: {
          ...state.manage,
          type,
          isOpen: !state.manage.isOpen,
          item,
        },
        claimToUserId: item?._user?._id,
      },
    });
  };

  const handleResolve = async (itemId: string) => {
    try {
      await resolveItem(itemId);
      enqueueSnackbar(staticTexts.itemResolvedSuccessfully, {
        variant: "success",
      });
      window.location.reload();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const handleWarningDialog = (_item: string) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        resolveWarning: !state.resolveWarning,
        _item,
      },
    });
  };

  const fetchLostItems = async (searchQuery: string = "", page: number = 1) => {
    try {
      const params: any = {
        page,
        limit: itemsPerPage,
        text: searchQuery,
        type: "LOST",
      };

      if (state.statusFilter !== "ALL") {
        params.status = state.statusFilter;
      }

      if (state.categoryFilter !== "ALL") {
        params.categoryId = state.categoryFilter;
      }

      const lostItems = (await getItems(params)) as any;

      if (!lostItems || !lostItems.items?.length) {
        dispatch({ type: "LOADED", list: [] });
        return;
      }

      dispatch({
        type: "UPDATE_FIELDS",
        payload: { totalPages: lostItems.meta.totalPages },
      });

      const list = lostItems.items.map((item: any, index: number) => {
        return createRow(item, index, handleManage, handleWarningDialog, page);
      });
      dispatch({ type: "LOADED", list });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  useEffect(() => {
    fetchLostItems(state.searchQuery, state.page);
  }, [state.statusFilter, state.categoryFilter]);

  const handleSearch = (e: ChangeEvent<HTMLElement>) => {
    const searchQuery = (e.target as HTMLInputElement).value;
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { searchQuery, page: 1 },
    });

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(() => {
      fetchLostItems(searchQuery, 1);
    }, 500);

    setDebounceTimeout(timeout);
  };

  const onPageChange = (e: ChangeEvent<unknown>, page: number) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { page },
    });
    fetchLostItems(state.searchQuery, page);
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateItemStatus(id, { status });
      dispatch({ type: "UPDATE_STATUS", _id: id, status });
      enqueueSnackbar(staticTexts.itemStatusUpdatedSuccessfully, {
        variant: "success",
      });
      dispatch({ type: 'TOGGLE_STATUS', payload: { id, status } });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    } finally {
      dispatch({ type: "UPDATE_FIELDS", payload : { loading : false} });
    }
  };

  const columns = [
    {
      id: "id",
      label: staticTexts.sNo,
    },
    {
      id: "name",
      label: staticTexts.itemName,
    },
    {
      id: "image",
      label: staticTexts.image,
    },
    {
      id: "location",
      label: staticTexts.location,
    },
    {
      id: "date",
      label: staticTexts.lostDate,
    },
    {
      id: "status",
      label: staticTexts.status,
    },
    {
      id: "action",
      label: staticTexts.action,
    },
  ];

  const statusFilter = [
    {
      label: staticTexts.all,
      value: "ALL",
    },
    {
      label: staticTexts.active,
      value: "ACTIVE",
    },
    {
      label: staticTexts.inactive,
      value: "INACTIVE",
    },
  ];

  const handleClose = () => {
    setShowChatListing(!showChatListing);
  };

  const fetchChatDetails = async (payload: object) => {
    try {
      console.log("paylod", payload);
      const list = await getChatListing(payload);
      dispatch({
        type: "UPDATE_FIELDS",
        payload: { userList: list, loading: false },
      });
    } catch (error) {
      enqueueSnackbar("Error in fetching chats");
    }
  };

  const createRow = (
    item: any,
    index: number,
    onEdit: any,
    onResolve: any,
    page: number
  ) => {
    const name = (
      <Typography
        className="cursor-pointer"
        color="primary"
        variant="body1"
        onClick={() => {
          onEdit("view", item);
        }}
        sx={{ textTransform: "capitalize" }}
      >
        {item.name}
      </Typography>
    );

    const image =
      item.images?.length > 0 ? (
        <Avatar src={item.images[0]} alt="item" />
      ) : (
        <Avatar sx={{ backgroundColor: "#FFE0B5" }}>
          <CameraAltIcon sx={{ color: "#4A403A" }} />
        </Avatar>
      );

    const status = (
      <Switch
        checked={item.status === "ACTIVE"}
        disabled={isEditIconDisabled}
        onChange={(e) => {
          handleStatusChange(
            item._id,
            e.target.checked ? "ACTIVE" : "INACTIVE"
          );
        }}
      />
    );

    const action = (
      <>
        <Tooltip title={staticTexts.resolve}>
          <Button
            variant="outlined"
            disabled={isEditIconDisabled}
            color="primary"
            size="small"
            sx={{
              minWidth: "auto",
              padding: "4px 8px",
              textTransform: "capitalize",
            }}
            onClick={() => {
              onResolve(item._id);
            }}
          >
            {staticTexts.resolve}
          </Button>
        </Tooltip>
      </>
    );

    return {
      id: (page - 1) * itemsPerPage + index + 1,
      name,
      image,
      location: handleCharacterLimit(item.location?.name || "", 20),
      date: formatDate(item.eventDate),
      claimCount: (
        <Typography align="center">{item.claimCount || 0}</Typography>
      ),
      status,
      action,
      _id: item._id,
    };
  };

  return (
    <>
      {state.loading && <Loader />}
      <Header
        searchPlaceholder={staticTexts.searchByItemName}
        onSearch={handleSearch}
        searchInput
        isEditIconDisabled={isEditIconDisabled}
        isDeleteIconDisabled={isDeleteIconDisabled}
      >
        <FormControl className="ml-2" size="small">
          <Select
            size="small"
            displayEmpty
            value={state.statusFilter}
            renderValue={(selected) => {
              if (!selected || selected === "") {
                return staticTexts.statusFilter;
              }
              if (selected === "ALL") {
                return staticTexts.all;
              }
              return selected === "ALL"
                ? staticTexts.all
                : selected === "ACTIVE"
                ? staticTexts.active
                : staticTexts.inactive;
            }}
            onChange={(e) => {
              dispatch({
                type: "UPDATE_FIELDS",
                payload: { statusFilter: e.target.value },
              });
            }}
          >
            {statusFilter.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {categories && categories.length > 0 && (
          <FormControl className="ml-2" size="small">
            <Select
              size="small"
              displayEmpty
              value={state.categoryFilter}
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return staticTexts.categoryFilter;
                }
                if (selected === "all") {
                  return staticTexts.allCategory;
                }
                const selectedCategory = categories?.find(
                  (cat: any) => cat._id === selected
                );
                return selectedCategory?.name?.[language]
                  .split(" ")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(" ");
              }}
              onChange={(e) =>
                dispatch({
                  type: "UPDATE_FIELDS",
                  payload: { categoryFilter: e.target.value, page: 1 },
                })
              }
            >
              {(categories || []).map((category: any) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name?.[language]
                    ?.split(" ")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Header>

      {/* Show Data  */}
      <Box marginTop="10px">
        <CustomTable
          columns={columns}
          rows={state.list}
          height="calc(100vh - 193px)"
          errorMessage={staticTexts.itemsNotAvailable}
          pagination={{
            page: state.page,
            totalPages: state.totalPages,
          }}
          onPageChange={onPageChange}
        />
      </Box>

      <Manage
        open={state.manage.isOpen}
        onClose={() => handleManage("view", null)}
        title={staticTexts.lostItemDetails}
        maxWidth="lg"
        titlePadding="16px 24px"
        contentPadding="0px 24px"
        fullWidth={true}
        content={
          <Box>
            {/* Item Details Section */}
            <Typography
              variant="h6"
              fontSize="16px"
              color="#4A403A"
              gutterBottom
            >
              {staticTexts.itemDetails}
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.itemName}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.location}
                  </Typography>
                  <Typography
                    component="p"
                    fontSize="14px"
                    color="#4A403A"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {state.manage.item?.location?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.category}
                  </Typography>
                  <Typography
                    component="p"
                    fontSize="14px"
                    color="#4A403A"
                    sx={{ textTransform: "capitalize" }}
                  >
                   {state.manage.item?._category?.name?.[language]}
                  </Typography>
                </Box>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.name}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?._user?.name}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.emailAddress}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?._user?.email || "N/A"}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.phoneNumber}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {`+${state.manage.item?._user?.phone?.prefix} ${state.manage.item?._user?.phone?.number}` ||
                      "N/A"}
                  </Typography>
                </Box>

                <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.lostDate}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {formatDate(state.manage.item?.eventDate)}
                  </Typography>
                </Box>

                {/* {state.manage.item?.questions?.map((question: any) => (
                  <Box key={question._question?._id} marginBottom="24px">
                    <Typography component="p" fontSize="12px" color="#776E65">
                      {question._question?.label}
                    </Typography>
                    <Typography component="p" fontSize="14px" color="#4A403A">
                      {question?.value || "N/A"}
                    </Typography>
                  </Box>
                ))} */}

                {/* <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    Last 4 digits of the account number{" "}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    XXXXXX1234{" "}
                  </Typography>
                </Box> */}
              </Grid2>
            </Grid2>
            <Box marginBottom="24px">
                  <Typography component="p" fontSize="12px" color="#776E65">
                    {staticTexts.description}
                  </Typography>
                  <Typography component="p" fontSize="14px" color="#4A403A">
                    {state.manage.item?.description || "N/A"}
                  </Typography>
                </Box>
            <Typography
              variant="h6"
              fontSize="16px"
              color="#4A403A"
              gutterBottom
            >
              {staticTexts.questionDetails}
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {state.manage.item?.questions?.map((question: any) => (
                  <Box key={question._question?._id} marginBottom="24px">
                    <Typography component="p" fontSize="12px" color="#776E65">
                      {language === "en"
                        ? question._question?.label?.en
                        : question._question?.label?.ar}
                    </Typography>
                    <Typography component="p" fontSize="14px" color="#4A403A">
                      {question?.value || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            </Grid2>

            {/* Uploaded Images Section */}
            {state.manage.item?.images &&
              state.manage.item.images.length > 0 && (
                <>
                  <Box mt={4}>
                    <Typography
                      variant="h6"
                      fontSize="16px"
                      color="#4A403A"
                      gutterBottom
                    >
                      {staticTexts.uploadedImages}
                    </Typography>
                    <Box display="flex" gap="8px">
                      {state.manage.item?.images?.map(
                        (imageUrl: string, index: number) => (
                          <img
                            key={index}
                            src={imageUrl}
                            alt={`Item ${index + 1}`}
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "8px",
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>
                </>
              )}

            <Box
              display="flex"
              gap="16px"
              justifyContent="end"
              width="100%"
              paddingBottom="24px"
            >
              {/* <Button
                sx={{
                  alignSelf: "flex-end",
                }}
                onClick={() => {
                  fetchChatDetails({
                    itemId: state.manage.item._id,
                    claimToUserId: state.claimToUserId,
                  });
                  setShowChatListing(true);
                  handleManage("view", null);
                }}
              >
                {staticTexts.seeChats}
              </Button> */}
            </Box>
          </Box>
        }
      />

      <WarningDialog
        isOpen={state.resolveWarning}
        onClose={() => handleWarningDialog(state._item || "")}
        onConfirm={() => handleResolve(state._item)}
        title={staticTexts.resolveItem}
        description={staticTexts.areYouSureDoYouWantToResolveThisItem}
      />

      <ChatDetails
        itemId={state._item}
        claimToUserId={state.claimToUserId}
        userList={state.userList}
        showChatListing={showChatListing}
        handleClose={handleClose}
      />
    </>
  );
};

export default withPermission(LostItems, PERMISSIONS.VIEW_LOST_ITEMS);
